import { ForgeAvaInspect } from '@/scripts/js/forgeModels';
import avaInspectService from '../services/avaInspect.service';
import store from '@/store';
import router from '@/router';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import { RegisterType } from '@/features/Registers/config/enums';
import {
  REGISTER_FILTER_EMPTY_TAG_ID,
  REGISTER_VIEWER_ACTION,
  normalizeRegisterType,
} from '@/features/Registers/config/registerViewerContext';
import { upsertTagById } from '@/features/Tags/utils/tagMarkerLifecycle';
import {
  NO_TAGS_FOUND_STATUS,
  activeDashboardIdFilter,
  dashboardFilterCount,
  dashboardIdPage,
  nextDashboardPage,
  scopedDashboardIds,
} from '@/features/Tags/utils/avaInspectDashboardFilter';

/** Parses a raw API tag into the list's model. */
function toAvaInspectModel(raw) {
  const tag = Object.assign(new ForgeAvaInspect(), raw);
  tag.parseContent();
  return tag;
}

/**
 * The register-scoped tag ids (the Register "view in viewer" filter), or null when that filter is
 * not active for AVA Inspects. An active filter with no ids stays fail-closed via the sentinel id.
 */
function registerScopedTagIds() {
  const viewerMode = store.getters['VIEWERS_MODE/OPTIONS'];
  if (viewerMode?.action !== REGISTER_VIEWER_ACTION
    || viewerMode.metadata?.hasFilter !== true
    || normalizeRegisterType(viewerMode.metadata?.registerType) !== RegisterType.AvaInspect) return null;
  const tagIds = viewerMode.metadata?.tagIds || [];
  return tagIds.length ? tagIds : [REGISTER_FILTER_EMPTY_TAG_ID];
}

/**
 * One page of the "dashboard filter" mode (KPI drill to list): the dashboard's ids, read through
 * getTagsByIds because the list's /Search query has no id filter. A page none of whose ids
 * resolves in this facility comes back as 400 (NotFound) and is an empty page, not a failure.
 */
async function fetchDashboardIdPage(pageIds, assetId, signal) {
  if (!pageIds.length) return [];
  try {
    const { data } = await avaInspectService.getTagsByIds(pageIds, assetId, signal);
    return Array.isArray(data) ? data : [];
  } catch (err) {
    if (err?.response?.status !== NO_TAGS_FOUND_STATUS) throw err;
    debugWarn('[AVA_INSPECT] none of the dashboard ids on this page resolved', { count: pageIds.length });
    return [];
  }
}

export const AVA_INSPECT = {
  namespaced: true,
  state: {
    // Tags
    avaInspectTags: [],
    avaInspectTagsCount: 0,
    filteredAvaInspectTagsIds: [],
    
    // Search
    searchText: '',
    
    // Filters
    selectedFilter: 'No Filter',
    availableColorOptions: [],
    selectedColorOptions: [],
    
    // Units
    units: [],
    
    // Master Type Options
    masterTypeOptions: [],
    
    // Master Tag
    selectedMasterTagId: null,
    
    // Date Filter
    dateFilterType: 'Year',
    dateFilterValue: null,
    
    // Zones
    zones: [],
    
    // Loading states
    isLoading: false,
    isTagReadOnly: true,
    editingTag: null,

    // AbortController for cancelling pending requests
    currentRequestController: null,

    // "Dashboard filter" mode (KPI drill to list): { ids, total, facilityId, requestId } or null,
    // and how many of its ids the list has consumed so far (see nextDashboardPage).
    dashboardIdFilter: null,
    dashboardIdOffset: 0,
  },
  
  getters: {
    GET_AVA_INSPECT_TAGS: (state) => state.avaInspectTags,
    GET_AVA_INSPECT_TAGS_COUNT: (state) => state.avaInspectTagsCount,
    GET_FILTERED_AVA_INSPECT_TAGS_IDS: (state) => state.filteredAvaInspectTagsIds,
    GET_SEARCH_TEXT: (state) => state.searchText,
    GET_SELECTED_FILTER: (state) => state.selectedFilter,
    GET_AVAILABLE_COLOR_OPTIONS: (state) => state.availableColorOptions,
    GET_SELECTED_COLOR_OPTIONS: (state) => state.selectedColorOptions,
    GET_UNITS: (state) => state.units,
    GET_MASTER_TYPE_OPTIONS: (state) => state.masterTypeOptions,
    GET_SELECTED_MASTER_TAG_ID: (state) => state.selectedMasterTagId,
    GET_DATE_FILTER_TYPE: (state) => state.dateFilterType,
    GET_DATE_FILTER_VALUE: (state) => state.dateFilterValue,
    GET_ZONES: (state) => state.zones,
    GET_IS_LOADING: (state) => state.isLoading,
    GET_IS_TAG_READ_ONLY: (state) => state.isTagReadOnly,
    GET_EDITING_TAG: (state) => state.editingTag,
    GET_CURRENT_REQUEST_CONTROLLER: (state) => state.currentRequestController,
    /** The dashboard id filter when it belongs to the current facility, otherwise null. */
    GET_ACTIVE_DASHBOARD_ID_FILTER: (state, getters, rootState) =>
      activeDashboardIdFilter(state.dashboardIdFilter, rootState.assetId),
    /** How many AVA Inspects the dashboard filter mode lists (the banner's N). */
    GET_DASHBOARD_FILTER_COUNT: (state, getters) => dashboardFilterCount(
      getters.GET_ACTIVE_DASHBOARD_ID_FILTER, state.dashboardIdOffset, state.avaInspectTagsCount,
    ),

    // Computed getters
    GET_SELECTED_UNITS: (state) => {
      return state.units.filter(u => u.isChecked).map(u => u.name);
    },
    GET_SELECTED_TYPE_OPTIONS: (state) => {
      return state.masterTypeOptions.filter(o => o.isChecked).map(o => o.name);
    },
    GET_SELECTED_ZONES: (state) => {
      return state.zones.filter(z => z.isChecked).map(z => z.id);
    },
    HAS_ACTIVE_FILTERS: (state, getters) => {
      return (
        getters.GET_SELECTED_UNITS.length < state.units.length ||
        getters.GET_SELECTED_TYPE_OPTIONS.length < state.masterTypeOptions.length ||
        getters.GET_SELECTED_ZONES.length < state.zones.length ||
        state.selectedMasterTagId ||
        state.selectedFilter !== 'No Filter' ||
        state.selectedColorOptions.length > 0 ||
        state.dateFilterValue
      );
    },
  },
  
  mutations: {
    SET_AVA_INSPECT_TAGS(state, value) {
      state.avaInspectTags = value;
    },
    SET_AVA_INSPECT_TAGS_COUNT(state, value) {
      state.avaInspectTagsCount = value;
    },
    SET_FILTERED_AVA_INSPECT_TAGS_IDS(state, value) {
      state.filteredAvaInspectTagsIds = value;
    },
    SET_SEARCH_TEXT(state, value) {
      state.searchText = value || '';
    },
    SET_SELECTED_FILTER(state, value) {
      state.selectedFilter = value || 'No Filter';
    },
    SET_AVAILABLE_COLOR_OPTIONS(state, value) {
      state.availableColorOptions = value;
    },
    SET_SELECTED_COLOR_OPTIONS(state, value) {
      state.selectedColorOptions = value;
    },
    SET_UNITS(state, value) {
      state.units = value.map(u => ({
        ...u,
        isChecked: u.isChecked !== undefined ? u.isChecked : true
      }));
    },
    SET_MASTER_TYPE_OPTIONS(state, value) {
      state.masterTypeOptions = value.map(o => ({
        ...o,
        isChecked: o.isChecked !== undefined ? o.isChecked : true
      }));
    },
    SET_SELECTED_MASTER_TAG_ID(state, value) {
      state.selectedMasterTagId = value;
    },
    SET_DATE_FILTER_TYPE(state, value) {
      state.dateFilterType = value;
    },
    SET_DATE_FILTER_VALUE(state, value) {
      state.dateFilterValue = value;
    },
    SET_ZONES(state, zones) {
      state.zones = zones.map(z => ({
        ...z,
        isChecked: z.isChecked !== undefined ? z.isChecked : true
      }));
    },
    TOGGLE_ZONE_SELECTION(state, zoneId) {
      const zone = state.zones.find(z => z.id === zoneId);
      if (zone) zone.isChecked = !zone.isChecked;
    },
    SET_IS_LOADING(state, value) {
      state.isLoading = value;
    },
    SET_IS_TAG_READ_ONLY(state, value) {
      state.isTagReadOnly = value;
    },
    SET_EDITING_TAG(state, value) {
      state.editingTag = value;
    },
    ADD_AVA_INSPECT_TAG(state, tag) {
      const outcome = upsertTagById(state.avaInspectTags, tag);
      if (outcome === 'inserted') state.avaInspectTagsCount++;
    },
    UPDATE_AVA_INSPECT_TAG(state, tag) {
      const index = state.avaInspectTags.findIndex(t => t.id === tag.id);
      if (index !== -1) {
        state.avaInspectTags.splice(index, 1, tag);
      }
    },
    REMOVE_AVA_INSPECT_TAG(state, tagId) {
      const index = state.avaInspectTags.findIndex(t => t.id === tagId);
      if (index !== -1) {
        state.avaInspectTags.splice(index, 1);
        state.avaInspectTagsCount--;
      }
    },
    CLEAR_FILTERS(state) {
      state.units.forEach(u => u.isChecked = true);
      state.masterTypeOptions.forEach(o => o.isChecked = true);
      state.zones.forEach(z => z.isChecked = true);
      state.selectedMasterTagId = null;
      state.selectedFilter = 'No Filter';
      state.selectedColorOptions = [];
      state.dateFilterType = 'Year';
      state.dateFilterValue = null;
    },
    SET_DASHBOARD_ID_FILTER(state, filter) {
      state.dashboardIdFilter = filter || null;
      state.dashboardIdOffset = 0;
    },
    CLEAR_DASHBOARD_ID_FILTER(state) {
      state.dashboardIdFilter = null;
      state.dashboardIdOffset = 0;
    },
    SET_DASHBOARD_ID_OFFSET(state, value) {
      state.dashboardIdOffset = value;
    },
    // Request cancellation mutations
    SET_REQUEST_CONTROLLER(state, controller) {
      state.currentRequestController = controller;
    },
    CLEAR_REQUEST_CONTROLLER(state) {
      state.currentRequestController = null;
    },
  },
  
  actions: {
    /**
     * Get a single AVA Inspect tag by ID with full details
     */
    GET_AVA_INSPECT_BY_ID({ rootState }, id) {
      return avaInspectService.getById(id, rootState.assetId).then(({ data }) => {
        let tag = Object.assign(new ForgeAvaInspect(), data);
        tag.parseContent();
        return tag;
      });
    },
    
    /**
     * Load AVA Inspect tags using the service with current filters
     * Supports request cancellation via AbortController
     */
    async LOAD_AVA_INSPECT_TAGS({ commit, state, getters }, reload = true) {
      // Cancel any existing request before starting a new one
      if (state.currentRequestController) {
        state.currentRequestController.abort();
      }

      // Create new AbortController for this request
      const controller = new AbortController();
      commit('SET_REQUEST_CONTROLLER', controller);

      const assetId = router.currentRoute.params.id;

      if (!assetId) {
        debugError('No assetId found in route');
        commit('CLEAR_REQUEST_CONTROLLER');
        return;
      }

      commit('SET_IS_LOADING', true);

      try {
        const registerIds = registerScopedTagIds();
        const dashboardFilter = getters.GET_ACTIVE_DASHBOARD_ID_FILTER;
        if (dashboardFilter) {
          const ids = scopedDashboardIds(dashboardFilter.ids, registerIds);
          const skip = reload ? 0 : state.dashboardIdOffset;
          const pageIds = dashboardIdPage(ids, skip);
          const fetched = await fetchDashboardIdPage(pageIds, assetId, controller.signal);
          const page = nextDashboardPage({
            ids,
            reload,
            offset: skip,
            listed: state.avaInspectTags,
            pageIds,
            fetched: fetched.map(toAvaInspectModel),
          });
          commit('SET_AVA_INSPECT_TAGS', page.tags);
          commit('SET_AVA_INSPECT_TAGS_COUNT', page.count);
          commit('SET_DASHBOARD_ID_OFFSET', page.offset);
          if (store.state.forgeService) store.state.forgeService.update3dIconsInAllViews();
          return page.tags;
        }

        // Build search query with filters
        const searchQuery = {
          assetId,
          search: state.searchText || '',
          units: getters.GET_SELECTED_UNITS,
          masterTypes: getters.GET_SELECTED_TYPE_OPTIONS,
          zones: getters.GET_SELECTED_ZONES,
          masterTagId: state.selectedMasterTagId,
          filterType: state.selectedFilter !== 'No Filter' ? state.selectedFilter : null,
          filterColors: state.selectedColorOptions,
          dateFilterType: state.dateFilterValue ? state.dateFilterType : null,
          dateFilterValue: state.dateFilterValue,
          skip: reload ? 0 : state.avaInspectTags.length,
          take: 50, // Load 50 tags at a time
        };
        if (registerIds) searchQuery.tagIds = registerIds;

        const { data } = await avaInspectService.search(searchQuery, controller.signal);

        // Parse tags
        const tags = data.items.map(t => {
          let tag = Object.assign(new ForgeAvaInspect(), t);
          tag.parseContent();
          return tag;
        });

        if (reload) {
          commit('SET_AVA_INSPECT_TAGS', tags);
        } else {
          // Append to existing tags
          commit('SET_AVA_INSPECT_TAGS', [...state.avaInspectTags, ...tags]);
        }

        commit('SET_AVA_INSPECT_TAGS_COUNT', data.total);

        // Update 3D viewer if forgeService is available
        if (store.state.forgeService) {
          store.state.forgeService.update3dIconsInAllViews();
        }

        return tags;
      } catch (err) {
        // Don't show error for cancelled requests
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          debugError('Failed to load AVA Inspect tags:', err);
          throw err;
        }
      } finally {
        commit('SET_IS_LOADING', false);
        commit('CLEAR_REQUEST_CONTROLLER');
      }
    },
    
    /**
     * Create a new AVA Inspect tag
     */
    async CREATE_AVA_INSPECT_TAG({ commit, dispatch }, tagData) {
      try {
        const { data } = await avaInspectService.add(tagData);
        let tag = Object.assign(new ForgeAvaInspect(), data);
        tag.parseContent();
        
        commit('ADD_AVA_INSPECT_TAG', tag);
        
        // Update 3D viewer
        if (store.state.forgeService) {
          store.state.forgeService.update3dIconsInAllViews();
        }
        
        return tag;
      } catch (error) {
        debugError('Failed to create AVA Inspect tag:', error);
        throw error;
      }
    },
    
    /**
     * Update an existing AVA Inspect tag
     */
    async UPDATE_AVA_INSPECT_TAG({ commit, dispatch }, { tagData, propagateUpdates = true }) {
      try {
        const { data } = await avaInspectService.update(tagData, propagateUpdates);
        let tag = Object.assign(new ForgeAvaInspect(), data);
        tag.parseContent();
        
        commit('UPDATE_AVA_INSPECT_TAG', tag);
        
        // Update 3D viewer
        if (store.state.forgeService) {
          store.state.forgeService.update3dIconsInAllViews();
        }
        
        return tag;
      } catch (error) {
        debugError('Failed to update AVA Inspect tag:', error);
        throw error;
      }
    },
    
    /**
     * Delete one or more AVA Inspect tags
     */
    async DELETE_AVA_INSPECT_TAGS({ dispatch }, ids) {
      try {
        return await dispatch('DELETE_AVA_INSPECT_TAG', { ids }, { root: true });
      } catch (error) {
        debugError('Failed to delete AVA Inspect tags:', error);
        throw error;
      }
    },
    
    /**
     * Get tags by multiple IDs
     */
    async GET_TAGS_BY_IDS({ commit }, ids) {
      let assetId = router.currentRoute.params.id;

      if (!assetId) {
        debugError('No assetId found in route');
        return [];
      }

      try {
        const { data } = await avaInspectService.getTagsByIds(ids, assetId);
        return data.map(t => {
          let tag = Object.assign(new ForgeAvaInspect(), t);
          tag.parseContent();
          return tag;
        });
      } catch (error) {
        debugError('Failed to get tags by IDs:', error);
        throw error;
      }
    },
    
    /**
     * Get minimal tag info for all tags (for efficient viewer loading)
     */
    async GET_ALL_TAGS_MINIMAL({ commit }) {
      const assetId = router.currentRoute.params.id;

      if (!assetId) {
        debugError('No assetId found in route');
        return [];
      }

      try {
        const { data } = await avaInspectService.getAllTagsMinimalInfo(assetId);
        return data;
      } catch (error) {
        debugError('Failed to get minimal tag info:', error);
        throw error;
      }
    },
    
    /**
     * Update available color options based on selected filter
     */
    UPDATE_AVAILABLE_FILTER_COLORS({ commit, state }) {
      const filterName = state.selectedFilter;
      
      if (!filterName || filterName === 'No Filter') {
        commit('SET_AVAILABLE_COLOR_OPTIONS', []);
        commit('SET_SELECTED_COLOR_OPTIONS', []);
        return;
      }
      
      // Get color options from main TAGS store based on filter type
      const tagsStore = store.state.TAGS;
      let colorOptions = [];
      
      switch (filterName) {
        case 'Coating Condition':
          colorOptions = tagsStore.CoatingConditionColorOptions || [];
          break;
        case 'Corrosion Condition':
          colorOptions = tagsStore.CorrosionConditionColorOptions || [];
          break;
        case 'Severity':
          colorOptions = tagsStore.SeverityColorOptions || [];
          break;
        default:
          colorOptions = [];
      }
      
      commit('SET_AVAILABLE_COLOR_OPTIONS', colorOptions);
    },
    
    INITIALIZE_UNITS({ commit }, units) {
      commit('SET_UNITS', units);
    },
    
    INITIALIZE_MASTER_TYPE_OPTIONS({ commit }, options) {
      commit('SET_MASTER_TYPE_OPTIONS', options);
    },
    
    INITIALIZE_ZONES({ commit }, zones) {
      commit('SET_ZONES', zones);
    },
    
    APPLY_FILTERS({ dispatch, state }) {
      // Trigger reload with current filter settings
      return dispatch('LOAD_AVA_INSPECT_TAGS', true);
    },
    
    CLEAR_ALL_FILTERS({ commit, dispatch }) {
      commit('CLEAR_FILTERS');
      return dispatch('APPLY_FILTERS');
    },

    /**
     * Cancel any pending request
     */
    CANCEL_CURRENT_REQUEST({ commit, state }) {
      if (state.currentRequestController) {
        state.currentRequestController.abort();
        commit('CLEAR_REQUEST_CONTROLLER');
        commit('SET_IS_LOADING', false);
      }
    },
  }
};
